import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    showOnXs = "showOnXs",
    showOnSm = "showOnSm",
    showOnMd = "showOnMd",
    showOnLg = "showOnLg",
    showOnXl = "showOnXl",
    cssClass = "cssClass",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showOnXs]: boolean;
        [fields.showOnSm]: boolean;
        [fields.showOnMd]: boolean;
        [fields.showOnLg]: boolean;
        [fields.showOnXl]: boolean;
        [fields.cssClass]: string;
    };
}

type Props = OwnProps & ThemeProps<BaseTheme>;

export interface ResponsiveWrapperStyles {
    wrapper?: InjectableCss;
}

export const responsiveWrapperStyles: ResponsiveWrapperStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
};

const SurResponsiveWrapper: FC<Props> = ({ id, fields, theme }) => {
    const { showOnXs, showOnSm, showOnMd, showOnLg, showOnXl, cssClass } = fields;

    const getResponsiveCss = () => {
        return css`
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    showOnXs
                        ? css`
                              display: block;
                          `
                        : css`
                              display: none;
                          `,
                    showOnSm
                        ? css`
                              display: block;
                          `
                        : css`
                              display: none;
                          `,
                    showOnMd
                        ? css`
                              display: block;
                          `
                        : css`
                              display: none;
                          `,
                    showOnLg
                        ? css`
                              display: block;
                          `
                        : css`
                              display: none;
                          `,
                    showOnXl
                        ? css`
                              display: block;
                          `
                        : css`
                              display: none;
                          `,
                ])}
        `;
    };

    const wrapperStyles: InjectableCss = {
        css: getResponsiveCss(),
    };

    return (
        <StyledWrapper {...wrapperStyles} className={cssClass || undefined}>
            <Zone contentId={id} zoneName="Content" />
        </StyledWrapper>
    );
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const responsiveWrapperWidget: WidgetModule = {
    component: withTheme(SurResponsiveWrapper),
    definition: {
        group: "Basic",
        icon: "Responsive",
        displayName: "Responsive Wrapper",
        fieldDefinitions: [
            {
                name: fields.showOnXs,
                displayName: "Show on XS (Extra Small - Mobile)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tooltip: "Display content on extra small screens (< 576px)",
            },
            {
                name: fields.showOnSm,
                displayName: "Show on SM (Small - Large Mobile/Small Tablet)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tooltip: "Display content on small screens (≥ 576px)",
            },
            {
                name: fields.showOnMd,
                displayName: "Show on MD (Medium - Tablet)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tooltip: "Display content on medium screens (≥ 768px)",
            },
            {
                name: fields.showOnLg,
                displayName: "Show on LG (Large - Desktop)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tooltip: "Display content on large screens (≥ 992px)",
            },
            {
                name: fields.showOnXl,
                displayName: "Show on XL (Extra Large - Large Desktop)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tooltip: "Display content on extra large screens (≥ 1200px)",
            },
            {
                name: fields.cssClass,
                displayName: "CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: advancedTab,
            },
        ],
    },
};

export default responsiveWrapperWidget;